import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import JSONEditor from "jsoneditor";
import _ from "lodash";
import "jsoneditor/dist/jsoneditor.css";
import { jsonEditorSchema } from "./JsonEditorSchema";

export const JsonEditor = forwardRef(({ json, onChange, setShouldUpdateUI, lastSavedJsonRef }, ref) => {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const editorModes = Object.freeze(["tree", "code"]);

  useEffect(() => {
    editorInstanceRef.current = new JSONEditor(editorRef.current, {
      mode: editorModes[0],
      modes: editorModes,
      search: true,
      enableSort: false,
      enableTransform: true,
      onEditable: function (node) {
        if (node.path?.includes("fullLine")) {
          return false;
        }
        return true;
      },
      onChange: () => {
        const updatedJson = editorInstanceRef.current.get();
        const hasChanges = !_.isEqual(updatedJson, lastSavedJsonRef.current);
        setShouldUpdateUI(hasChanges);
      },
      onValidate: (json) => {
        const errors = [];

        const validateField = (field, path) => {
          const rules = field.rules || [];
          const rulesParams = field.rulesParams || {};

          Object.keys(rulesParams).forEach((ruleKey) => {
            if (!rules.includes(ruleKey)) {
              errors.push({
                path: [...path, "rulesParams", ruleKey],
                message: `rulesParams includes "${ruleKey}", which is not listed in rules`
              });
            }
          });
        };

        if (json && typeof json === "object" && Array.isArray(json?.formSections)) {
          json.formSections.forEach((section, sectionIndex) => {
            section.rows?.forEach((row, rowIndex) => {
              row.fields?.forEach((field, fieldIndex) => {
                const fieldPath = ["formSections", sectionIndex, "rows", rowIndex, "fields", fieldIndex];
                validateField(field, fieldPath);
              });
            });
          });
        }

        return errors;
      },
      schema: jsonEditorSchema
    });

    editorInstanceRef.current.set(json);
    editorInstanceRef.current.expandAll();

    return () => {
      editorInstanceRef.current.destroy();
    };
  }, [json]);

  useImperativeHandle(ref, () => ({
    handleSave() {
      const updatedJson = editorInstanceRef.current.get();
      onChange(updatedJson);
      lastSavedJsonRef.current = updatedJson;
      setShouldUpdateUI(false);
    }
  }));

  return <div ref={editorRef} style={{ height: "100vh", overflowX: "auto", whiteSpace: "nowrap" }} />;
});
